// Docs: IMPLEMENTATION.md §3.2. Client-side persistence of the TanStack Query
// cache to localStorage so the app paints last-known data on cold relaunch
// (especially the iOS Home Screen standalone app, which iOS frequently kills
// and cold-boots). On relaunch the Home + Inbox render from the stored cache
// immediately, then revalidate in the background — the two boot endpoints
// (get_inbox, get_stats) each take ~1.8-2.0s server-side, so keeping them off
// the critical path is the single biggest cold-start win. That only holds
// while the snapshot is fresh: past STALE_AFTER_MS the Railway api has slept
// and components/WakeGate.jsx hides the restored data until it has refetched.
//
// Update this file + queryClient.js together when changing what is cached
// across sessions or the gcTime/maxAge pairing.
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { defaultShouldDehydrateQuery } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { clearHintFormsCache } from './utils/hintFormsCache';

// Bump the version suffix if the persisted shape ever needs a hard reset
// independent of the deploy buster below.
export const PERSIST_CACHE_KEY = 'gt-rq-cache-v1';

// Only durable, safe-to-restore read models are persisted. Anything volatile,
// flow-specific, or large (audio/image blobs under `questionAudio`,
// `podcasts` transcripts) is deliberately excluded so the stored payload stays
// small and no stale in-flight state is ever resurrected. Keyed on the first
// segment of the query key (see queryKeys.js).
const PERSISTED_QUERY_ROOTS = new Set([
  'inbox',        // daily Inbox feed — the primary first paint
  'stats',        // Home daily ring + streak summary (slow endpoint)
  'preferences',  // prefs + day-window
  'enums',        // static type lists
  'languages',    // static language list
  'words',        // collection list/detail (small JSON)
  'lessons',      // active lessons / follow-ups
  'genus',        // tier/progress metadata
]);

const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: PERSIST_CACHE_KEY,
  // Coalesce rapid cache writes so we touch localStorage at most ~1x/sec.
  throttleTime: 1000,
});

export const persistOptions = {
  persister,
  // Discard the whole snapshot once it is a day old — a relaunch after that
  // refetches from scratch rather than showing very stale data.
  maxAge: 24 * 60 * 60 * 1000,
  // The release string changes on every deploy, which invalidates the stored
  // blob so a changed cache shape never rehydrates into new code. Falls back to
  // a constant when VITE_VERSION is unset (dev / un-versioned builds).
  buster: import.meta.env.VITE_VERSION || 'dev',
  dehydrateOptions: {
    shouldDehydrateQuery: (query) =>
      defaultShouldDehydrateQuery(query) &&
      PERSISTED_QUERY_ROOTS.has(query.queryKey?.[0]),
  },
};

// The persisted cache lives on the device, not the account, so it MUST be
// wiped whenever the JWT is cleared — otherwise the next user to sign in on the
// same device rehydrates the previous user's data. Call this at every logout /
// 401 site alongside `localStorage.removeItem('jwt')`.
export function clearPersistedCache() {
  try {
    queryClient.clear();
  } catch { /* ignore */ }
  // Hint forms are held outside TanStack Query (the drill renderers fetch them
  // directly), but they are per-user all the same.
  try {
    clearHintFormsCache();
  } catch { /* ignore */ }
  try {
    window.localStorage.removeItem(PERSIST_CACHE_KEY);
  } catch { /* ignore */ }
}
